import parsePhoneNumberFromString, { getCountryCallingCode } from "libphonenumber-js";

/**
 * Returns a phone number in E.164 format (+<callingCode><number>).
 *
 * Resolution order (isValid pass first, then isPossible pass for test/dummy numbers):
 *  1. rawPhoneNumber is already valid E.164 → return as-is.
 *  2. rawPhoneNumber digits prefixed with "+" are valid E.164 → return that
 *     (handles numbers that are correct but missing the leading +).
 *  3. Build from countryCode, which may be an ISO alpha-2 ("US"), a numeric
 *     calling code ("1"), or a prefixed calling code ("+1").
 *  4–6. Repeat steps 1–3 accepting isPossible() for test/dummy numbers with fake area codes.
 *  7. Return fallback.
 */
export function formatToE164(rawPhoneNumber, countryCode, fallback = '') {
    if (rawPhoneNumber == null) return fallback;

    const raw = String(rawPhoneNumber).trim();
    if (!raw) return fallback;

    const digits = raw.replace(/\D/g, '');
    if (!digits) return fallback;

    const getConstructed = () => {
        if (!countryCode) return null;
        try {
            const callingCode = /^\+?\d+$/.test(String(countryCode))
                ? String(countryCode).replace(/\D/g, '')
                : getCountryCallingCode(countryCode);
            return parsePhoneNumberFromString(`+${callingCode}${digits}`);
        } catch (_) {
            return null;
        }
    };

    const parsed = parsePhoneNumberFromString(raw);
    const parsedWithPlus = parsePhoneNumberFromString(`+${digits}`);
    const constructed = getConstructed();

    // Pass 1: strict validation
    if (parsed?.isValid()) return parsed.number;
    if (parsedWithPlus?.isValid()) return parsedWithPlus.number;
    if (constructed?.isValid()) return constructed.number;

    // Pass 2: length-only check to accept test/dummy numbers with fake area codes
    if (parsed?.isPossible()) return parsed.number;
    if (parsedWithPlus?.isPossible()) return parsedWithPlus.number;
    if (constructed?.isPossible()) return constructed.number;

    return fallback;
}
